/**
 * Timezone-aware date formatting. The workspace stores its operating
 * timezone (e.g. America/Sao_Paulo); these helpers display dates in that
 * zone instead of the viewer's browser locale.
 */

const RELATIVE_RTF = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

const SHORT_TIME_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

const SHORT_TIME_WITH_TZ_OPTS: Intl.DateTimeFormatOptions = {
  ...SHORT_TIME_OPTS,
  timeZoneName: "short",
};

/** Format an ISO string or epoch ms in the workspace's IANA timezone. */
export function formatInZone(
  ts: number | string | Date,
  timezone: string,
  withTz = false
): string {
  const date = typeof ts === "object" ? ts : new Date(ts);
  return new Intl.DateTimeFormat(undefined, {
    ...(withTz ? SHORT_TIME_WITH_TZ_OPTS : SHORT_TIME_OPTS),
    timeZone: timezone,
  }).format(date);
}

/** "5 minutes ago" / "in 2 days" — locale-aware. */
export function formatRelativeTime(ts: number | string | Date): string {
  const date = typeof ts === "object" ? ts : new Date(ts);
  const diffMs = date.getTime() - Date.now();
  const absSec = Math.abs(diffMs) / 1000;

  if (absSec < 60) return RELATIVE_RTF.format(Math.round(diffMs / 1000), "second");
  if (absSec < 3600) return RELATIVE_RTF.format(Math.round(diffMs / 60000), "minute");
  if (absSec < 86400) return RELATIVE_RTF.format(Math.round(diffMs / 3600000), "hour");
  if (absSec < 30 * 86400) return RELATIVE_RTF.format(Math.round(diffMs / 86400000), "day");
  return RELATIVE_RTF.format(Math.round(diffMs / (30 * 86400000)), "month");
}

/** Verify an IANA timezone string is supported by the runtime. */
export function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

/** Common IANA zones for the timezone picker — kept short. */
export const COMMON_TIMEZONES = [
  "America/Sao_Paulo",
  "America/New_York",
  "America/Los_Angeles",
  "America/Chicago",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Singapore",
  "Australia/Sydney",
  "UTC",
];
